import React, { useState, useEffect } from 'react';
import { tags } from '../utils/api';
import { useEscapeKey } from '../hooks/useEscapeKey';
import TagBadge from './Documents/atoms/TagBadge';

const TAILWIND_COLORS = [
  { name: 'red', label: 'Red', hex: '#EF4444' },
  { name: 'yellow', label: 'Yellow', hex: '#EAB308' },
  { name: 'green', label: 'Green', hex: '#22C55E' },
  { name: 'blue', label: 'Blue', hex: '#3B82F6' },
  { name: 'indigo', label: 'Indigo', hex: '#6366F1' },
  { name: 'purple', label: 'Purple', hex: '#A855F7' },
  { name: 'pink', label: 'Pink', hex: '#EC4899' },
  { name: 'gray', label: 'Gray', hex: '#6B7280' }
];

// Hex values for each intensity level
const INTENSITIES = {
  '100': { red: '#FEE2E2', yellow: '#FEF3C7', green: '#DCFCE7', blue: '#DBEAFE', indigo: '#E0E7FF', purple: '#F3E8FF', pink: '#FCE7F3', gray: '#F3F4F6' },
  '200': { red: '#FECACA', yellow: '#FDE68A', green: '#BBF7D0', blue: '#BFDBFE', indigo: '#C7D2FE', purple: '#E9D5FF', pink: '#FBCFE8', gray: '#E5E7EB' },
  '300': { red: '#FCA5A5', yellow: '#FCD34D', green: '#86EFAC', blue: '#93C5FD', indigo: '#A5B4FC', purple: '#D8B4FE', pink: '#F9A8D4', gray: '#D1D5DB' },
  '400': { red: '#F87171', yellow: '#FBBF24', green: '#4ADE80', blue: '#60A5FA', indigo: '#818CF8', purple: '#C084FC', pink: '#F472B6', gray: '#9CA3AF' },
  '500': { red: '#EF4444', yellow: '#F59E0B', green: '#22C55E', blue: '#3B82F6', indigo: '#6366F1', purple: '#A855F7', pink: '#EC4899', gray: '#6B7280' },
  '600': { red: '#DC2626', yellow: '#D97706', green: '#16A34A', blue: '#2563EB', indigo: '#4F46E5', purple: '#9333EA', pink: '#DB2777', gray: '#4B5563' },
  '700': { red: '#B91C1C', yellow: '#B45309', green: '#15803D', blue: '#1D4ED8', indigo: '#4338CA', purple: '#7E22CE', pink: '#BE185D', gray: '#374151' },
  '800': { red: '#991B1B', yellow: '#92400E', green: '#166534', blue: '#1E40AF', indigo: '#3730A3', purple: '#6B21A8', pink: '#9D174D', gray: '#1F2937' }
};

const INTENSITY_LIST = Object.keys(INTENSITIES);

// Helper function to determine text color based on background intensity
const getTextColor = (intensity) => {
  const intensityNum = parseInt(intensity);
  return intensityNum >= 500 ? 'text-white' : 'text-gray-900';
};


export default function CreateTagModal({ isOpen, onClose, onTagCreated, initialName = '', initialColor = '', editMode = false, tagId = null }) {
  useEscapeKey(onClose, isOpen);
  const [name, setName] = useState('');
  const [selectedColorName, setSelectedColorName] = useState('blue');
  const [selectedIntensity, setSelectedIntensity] = useState('500');
  const [error, setError] = useState('');

  // Helper function to find color and intensity from hex value
  const findColorAndIntensity = (hexColor) => {
    for (const [intensity, colors] of Object.entries(INTENSITIES)) {
      for (const [colorName, hex] of Object.entries(colors)) {
        if (hex.toLowerCase() === hexColor.toLowerCase()) {
          return { colorName, intensity };
        }
      }
    }
    return { colorName: 'blue', intensity: '500' }; // Default values
  };

  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      if (initialColor) {
        const { colorName, intensity } = findColorAndIntensity(initialColor);
        setSelectedColorName(colorName);
        setSelectedIntensity(intensity);
      }
    }
  }, [isOpen, initialName, initialColor]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const color = INTENSITIES[selectedIntensity][selectedColorName];
      const result = editMode 
        ? await tags.update(tagId, name.trim(), color)
        : await tags.create(name.trim(), color);
      onTagCreated(result);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create tag');
    }
  };

  if (!isOpen) return null;

  const previewColor = INTENSITIES[selectedIntensity][selectedColorName];

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-[95%] max-w-md shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium leading-6 text-gray-900">{editMode ? 'Edit Tag' : 'Create Tag'}</h3>
          <div className="mt-4">
            <div className="mb-4">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Tag Name
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preview
              </label>
              <div className="mb-3">
                <TagBadge
                  name={name || 'Tag Preview'}
                  color={previewColor}
                  interactive={false}
                />
              </div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Color
              </label>
              <div className="grid grid-cols-4 sm:grid-cols-8 gap-3">
                {TAILWIND_COLORS.map(color => (
                  <div key={color.name} className="relative">
                    <button
                      type="button"
                      className={`w-8 h-8 sm:w-6 sm:h-6 rounded-full bg-${color.name}-500 ${getTextColor('500')} hover:ring-2 hover:ring-offset-2 hover:ring-${color.name}-500 ${selectedColorName === color.name ? 'ring-2 ring-offset-2 ring-' + color.name + '-500' : ''}`}
                      onClick={() => setSelectedColorName(color.name)}
                      title={color.label}
                    />
                  </div>
                ))}
              </div>
              <div className="mt-3 grid grid-cols-4 sm:grid-cols-8 gap-3">
                {INTENSITY_LIST.map(intensity => (
                  <div key={intensity} className="relative">
                    <button
                      type="button"
                      className={`w-8 h-8 sm:w-6 sm:h-6 rounded-full bg-${selectedColorName}-${intensity} ${getTextColor(intensity)} hover:ring-2 hover:ring-offset-2 hover:ring-${selectedColorName}-${intensity} ${selectedIntensity === intensity ? 'ring-2 ring-offset-2 ring-' + selectedColorName + '-' + intensity : ''}`}
                      onClick={() => setSelectedIntensity(intensity)}
                      title={`Intensity ${intensity}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {error && (
              <div className="mb-4 text-sm text-red-600">
                {error}
              </div>
            )}

            <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  handleSubmit(e);
                }}
                className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {editMode ? 'Save Changes' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
